import connect from 'components/Shared/connect';
import { SortOrder } from 'models/SortOrder';
import styles from 'components/Filter/themes/BGH-RD3/desktop/styles.scss';
import { SortButton } from 'components/Filter/themes/BGH/types/sortButton';
import sortStyles from './sort.scss';
import { rm, NO_ELEMENT } from 'utils';
import cn from 'classnames';
import { fetchSortedPage } from 'views/ProductlistPage/actions';
import { SetStateAction, Dispatch } from 'react';
import { getSelectedSortOrder, getSortOrders } from 'views/ProductlistPage/selectors';
import { DropdownButton } from './dropdownButton';

interface SetSortOrderProps {
  setSortOrder: (sortOrder: number) => void;
}

interface SetSubfilterProp {
  setSubfilter: Dispatch<SetStateAction<SubFilterProps>>;
}

interface SortOrders extends SetSortOrderProps, SetSubfilterProp {
  sortOrders: number[];
  isOpen: boolean;
}

interface SubFilterProps {
  current: string;
  active: boolean;
}

interface SortProps extends SortOrders, SetSubfilterProp {
  selectedSortOrder: number;
  subfilter: SubFilterProps;
}

interface ContainerProps {
  children: React.ReactNode;
  className?: string;
  isRight?: boolean;
}

interface OwnProps extends SetSubfilterProp {
  subfilter: SubFilterProps;
}

const Container = ({ children, className, isRight }: ContainerProps) => (
  <div className={cn(styles.container, className, { [styles.right]: isRight })}>{children}</div>
);

const getTranslatedSortOrder = ({ sort }: { sort: number }) =>
  rm(`sortorder_${SortOrder[sort].toLowerCase()}`);

interface OnClickSortOrder {
  setSortOrder: () => void;
  setSubfilter: () => void;
}

const SortList = ({ sortOrders, isOpen, setSortOrder, setSubfilter }: SortOrders) => {
  if (!isOpen) {
    return NO_ELEMENT;
  }

  const onClick =
    ({ setSortOrder, setSubfilter }: OnClickSortOrder) =>
    () => {
      setSortOrder();
      setSubfilter();
    };

  const sortOptions = sortOrders.map(sortOrder => (
    <SortButton
      key={sortOrder}
      onClick={onClick({
        setSortOrder: () => setSortOrder(sortOrder),
        setSubfilter: () => setSubfilter({ active: false, current: '' })
      })}
      className={styles.option}
      textClassName={styles.sortOption}
      text={getTranslatedSortOrder({ sort: sortOrder })}
      sortOrder={sortOrder}
    />
  ));

  return (
    <Container className={sortStyles.sortDropdown} isRight>
      {sortOptions}
    </Container>
  );
};

const SORT_KEY = 'sort';

export const Sort = connect<SortProps, OwnProps>(
  ({ currentPage }) => ({
    sortOrders: getSortOrders(currentPage),
    selectedSortOrder: getSelectedSortOrder(currentPage)
  }),
  dispatch => ({
    setSortOrder: (sortOrder: SortOrder) => dispatch(fetchSortedPage(sortOrder))
  })
)(({ sortOrders, selectedSortOrder, setSubfilter, subfilter, setSortOrder }: SortProps) => {
  const displayName = getTranslatedSortOrder({ sort: selectedSortOrder });
  const isOpen = SORT_KEY === subfilter.current;
  const shouldClose = subfilter.active && isOpen;
  const onClick = () =>
    setSubfilter({
      current: SORT_KEY,
      active: !shouldClose
    });

  return (
    <div className={cn(styles.filterPanelContainer, styles.filterPanelContainerSort)}>
      <DropdownButton
        onClick={onClick}
        shouldClose={shouldClose}
        displayName={displayName}
        isSortButton
      />
      <SortList
        sortOrders={sortOrders}
        isOpen={isOpen && shouldClose}
        setSortOrder={setSortOrder}
        setSubfilter={setSubfilter}
      />
    </div>
  );
});
